import { useState } from "react";
import { useSearchParams } from "react-router";
import { Avatar, Button, CheckList, Dialog, FormRow, FormSection, HelpList, Icon, More, Pill, formatDate, formatRelative } from "@omnabase/ui";
import { MODEL_HINT, type AgentId, type Connection } from "@omnabase/shared";
import { api } from "../../lib/api";
import { useSetParam } from "../../lib/nav";
import { aiAppLine, appMark, CONNECTOR_CHECKLIST, isExpired, kindLabel, maskKey, renewDue, START_LINE } from "../../lib/connections";
import { useSession } from "../../shell/store";
import { KeyAgentSteps } from "./AgentSetup";

// M9 round one, "your own AI" (brief docs/briefs/m9-your-own-agent.md, task 7; ported from the prototype's screens/ConnectedAi.tsx): plain
// keys; round two lists the signed-in apps beside them (no end date: a signed-in app stays until it is revoked or unused for 30 days). The
// connector address, the member's keys, New key (named, then the key shown once with the setup steps), Renew near the end, Revoke. The
// "Your own AI" round moved it from the profile to Settings › Your own AI (since 2026-10-18 the MCP tab of Settings › AI), the chosen agent's lines in the key dialog.
// The one-card round (2026-10-23): the tables, the address and the key dialogs below the tab's card (`AiMcp.tsx`).

/** One connection as a table row (M14 round five, the founder: "tables instead of card lists", a column per fact): what it is called (with
 *  whose it is in the members' list), how it connects (a key with its last four characters, or the signed-in app) with the app it was last
 *  seen from and the model it last reported (M9 "which AI did this"; the model as reported by the AI), when it was made, when its AI last
 *  called, its state (when it ends, expired, paused while the owner's switch is off), and what can be done. */
export function ConnectionRow({ c, now, member, paused, onRenew, onRevoke }: { c: Connection; now: number; /** the members' list: whose */ member?: { name: string; initials: string; photo?: string }; paused?: boolean; onRenew?: () => void; onRevoke: () => void }) {
  const app = aiAppLine(c), mark = appMark(c.app); // the ticket page and marks round: the app's mark before its name
  const name = member
    ? <span className="ob-dtable__person"><Avatar size="sm" initials={member.initials} src={member.photo} /><span className="ob-dtable__who"><span className="ob-dtable__name" title={c.name}>{c.name}</span><span className="ob-dtable__email" title={member.name}>{member.name}</span></span></span>
    : <span className="ob-dtable__name" title={c.name}>{c.name}</span>;
  return (
    <tr className="ob-connections__row">
      <td className="ob-dtable__grow">{name}</td>
      <td className="ob-dtable__col--below-540"><span className="ob-dtable__who"><span>{kindLabel(c)}{c.kind === "key" && c.keyLast4 && <> · <code>{maskKey(c.keyLast4)}</code></>}</span>{app && <span className="ob-dtable__email ob-connections__app" title={c.lastModel ? MODEL_HINT : undefined}>{mark && <Icon name={mark} size="sm" />}{app}</span>}</span></td>
      <td className="ob-dtable__muted ob-dtable__col--below-820">{formatDate(c.createdAt)}</td>
      <td className="ob-dtable__muted ob-dtable__col--below-680">{c.lastUsedAt ? formatRelative(c.lastUsedAt, now) : "Never"}</td>
      <td className="ob-dtable__muted ob-connections__state">{c.kind === "oauth" ? "Signed in" : isExpired(c, now) ? <Pill>Expired</Pill> : <>Ends {formatDate(c.expiresAt)}</>}{paused && <> <Pill>Paused</Pill></>}</td>
      <td className="ob-dtable__actions ob-connections__actions">
        {onRenew && renewDue(c, now) && <Button size="sm" onClick={onRenew} aria-label={`Renew ${c.name}`}>Renew</Button>}
        <Button variant="ghost" size="sm" onClick={onRevoke} aria-label={`Revoke ${c.name}`}>Revoke</Button>
      </td>
    </tr>
  );
}

/** The table around the rows: the members' list puts the member in each row (`withMember`). */
export function ConnectionsTable({ label, withMember, children }: { label: string; withMember?: boolean; children: React.ReactNode }) {
  return (
    <div className="ob-dtable-card">
      <table className="ob-dtable ob-connections" aria-label={label}>
        <thead><tr><th>{withMember ? "Member and connection" : "Name"}</th><th className="ob-dtable__col--below-540">Key or app</th><th className="ob-dtable__col--below-820">Made</th><th className="ob-dtable__col--below-680">Last used</th><th>State</th><th></th></tr></thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

/** Renew and Revoke on the member's own connections (the MCP tab's tables), each with its toast; the workspace read's `me.connections`
 *  ("Open in Claude" in the ticket's ⋯ menu) follows a revoke */
export function useOwnConnectionActions(reloadList: () => Promise<void>) {
  const { workspace, toast, reload } = useSession();
  const renew = async (c: Pick<Connection, "id" | "name">) => {
    try { const r = await api.post<{ connection: Connection }>(`/api/v1/w/${workspace.slug}/me/connections/${c.id}/renew`); toast(`${c.name} renewed until ${formatDate(r.connection.expiresAt)}`, { kind: "success" }); await reloadList(); } catch (e) { toast((e as Error).message, { kind: "error" }); }
  };
  const revoke = async (c: Pick<Connection, "id" | "name">) => {
    try { await api.del(`/api/v1/w/${workspace.slug}/me/connections/${c.id}`); toast(`${c.name} revoked`, { kind: "success" }); await reloadList(); void reload(); } catch (e) { toast((e as Error).message, { kind: "error" }); }
  };
  return { renew, revoke };
}

/** The connector's address, once on the tab (the one-card round: above the table), with Copy */
export function ConnectorAddress({ address }: { address: string }) {
  const copy = useCopy();
  return (
    <FormRow label="Connector address" help="The address your AI connects to; another workspace has its own.">
      <span className="ob-card-line ob-connector-address"><code className="ob-code">{address}</code><Button size="sm" icon="copy" aria-label="Copy the connector address" onClick={() => copy(address, "Connector address")}>Copy</Button></span>
    </FormRow>
  );
}

/** A member's table (the one-card round, 2026-10-23: "Your connections" for anyone without `team`; an administrator's own rows sit in
 *  "Members' connections"): the address, their keys and signed-in apps (Renew, Revoke) and "Make a key". While the owner's switch is off the
 *  connections stay listed, paused, and no key can be made. */
export function YourConnections({ open, list, address }: { /** the plan has it and the switch is on */ open: boolean; list: { rows: Connection[]; loaded: boolean; reload: () => Promise<void> }; address: string }) {
  const setParam = useSetParam();
  const { renew, revoke } = useOwnConnectionActions(list.reload);
  const now = Date.now();
  if (!open && list.rows.length === 0) return null;
  return (
    <FormSection title="Your connections" intro={<>Your keys and the apps you signed in, in this workspace.<HelpList items={CONNECTION_RULES} /></>}>
      {open && address && <ConnectorAddress address={address} />}
      {list.rows.length > 0 && (
        <ConnectionsTable label="Your connections">{list.rows.map((c) => <ConnectionRow key={c.id} c={c} now={now} paused={!open} onRenew={open ? () => void renew(c) : undefined} onRevoke={() => void revoke(c)} />)}</ConnectionsTable>
      )}
      {open && list.loaded && list.rows.length === 0 && <p className="ob-form-row__help">Nothing connected yet. Connect Claude above, or make a key for an app that takes one.</p>}
      {open && <div className="ob-form__foot"><Button icon="key" onClick={() => setParam("newkey", "1", true)}>Make a key</Button></div>}
    </FormSection>
  );
}

/** "Make a key" (the card's menu, or a member's button beside the table): the New key dialog while `?newkey=1`, then the key once with the
 *  chosen app's lines. `onMade` reloads the tables. */
export function KeyDialogs({ agent, address, onMade }: { agent: AgentId; address: string; onMade: () => Promise<void> }) {
  const { reload } = useSession();
  const setParam = useSetParam(); const [params] = useSearchParams();
  const [shown, setShown] = useState<string | null>(null); // the new key, in memory only until Done
  const copy = useCopy();
  const created = async (key: string) => { setParam("newkey", null); setShown(key); await onMade(); void reload(); };
  return (
    <>
      {params.get("newkey") === "1" && <NewKeyDialog onClose={() => setParam("newkey", null)} onCreated={(key) => void created(key)} />}
      {shown && <KeyShownDialog agent={agent} secret={shown} address={address} onCopy={copy} onClose={() => setShown(null)} />}
    </>
  );
}

/** a connection's life, one rule per line */
const CONNECTION_RULES = ["A key works for 90 days; renew it near its end", "Revoking one stops its AI at once", "Connections take no seat and use no credits"];

/** copy a line and say so */
export function useCopy() {
  const { toast } = useSession();
  return (text: string, what: string) => { void navigator.clipboard?.writeText(text); toast(`${what} copied`, { kind: "success" }); };
}

/** What the member's AI may and may not do (brief task 4: the setup page says so; the founder, 2026-10-22: the consent screen's checklist),
 *  and what is outside our hands behind More (the settings copy rule: legal-ish sentences collapsed). */
export function ConnectedAiDataNote() {
  return (
    <>
      <CheckList label="What your AI may do" items={CONNECTOR_CHECKLIST} />
      <More>Your workspace answers for its customers' data; which AI provider you use is your workspace's choice. Other connectors in your AI app are outside our control: they can see what this one returns. {START_LINE}</More>
    </>
  );
}

/** Name it; the key comes back from the server once. */
function NewKeyDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (key: string) => void }) {
  const { workspace, toast } = useSession();
  const [name, setName] = useState(""); const [busy, setBusy] = useState(false);
  const create = async () => {
    if (!name.trim() || busy) return; setBusy(true);
    try { const r = await api.post<{ key: string }>(`/api/v1/w/${workspace.slug}/me/connections`, { name: name.trim() }); onCreated(r.key); }
    catch (e) { toast((e as Error).message, { kind: "error" }); setBusy(false); }
  };
  return (
    <Dialog title="New key" intro="For an app that takes a key instead of signing in (Claude Code, Codex, Cursor, scheduled runs). Name it after where it runs, so you know what a revoke stops." width={560} onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" disabled={!name.trim() || busy} onClick={() => void create()}>Create key</Button></>}>
      <label className="ob-dialog__label" htmlFor="ai-key-name">Name</label>
      <input id="ai-key-name" className="ob-dialog__input" autoFocus maxLength={120} value={name} placeholder="Claude Code on my Mac, nightly run…" onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") void create(); }} />
      <ConnectedAiDataNote />
    </Dialog>
  );
}

/** The key once, with the chosen agent's lines; after Done it is nowhere on the page again. */
function KeyShownDialog({ agent, secret, address, onCopy, onClose }: { agent: AgentId; secret: string; address: string; onCopy: (text: string, what: string) => void; onClose: () => void }) {
  return (
    <Dialog title="Copy your key now" intro="Shown only this once; later your list shows its last four characters. Whoever holds it acts as you here. It works for 90 days and renews on Your own AI." width={680} onClose={onClose} footer={<Button variant="primary" onClick={onClose}>Done</Button>}>
      <div className="ob-secret"><code aria-label="The new key">{secret}</code><Button size="sm" icon="copy" onClick={() => onCopy(secret, "Key")}>Copy</Button></div>
      <KeyAgentSteps agent={agent} address={address} secret={secret} onCopy={onCopy} />
      <ConnectedAiDataNote />
    </Dialog>
  );
}
